import type { ReturnSchedulingRequestView } from '@/dashboard/api';
import { createPortal } from 'react-dom';
import { WorkPanel } from '@/dashboard/ui/MasterDetail';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BellRing } from 'lucide-react';
import {
  advanceLeadStage,
  revealLeadContact,
  fetchMyWorkActionsResult,
  transitionWorkAction,
} from '@/dashboard/api';
import LeadContactRevealControl from '@/dashboard/owner/LeadContactReveal';
import LeadQuickViewHost from '@/dashboard/leads/LeadQuickViewHost';
import QuickViewStageStrip from '@/dashboard/leads/QuickViewStageStrip';
import { AcButton, EmptyState, ErrorState, LoadingState } from '@/dashboard/ui';
import DataTable from '@/dashboard/ui/DataTable';
import ModalShell from '@/dashboard/ui/ModalShell';
import RegisterBookingOffer from '@/dashboard/patients/RegisterBookingOffer';
import { walkinBoardHref } from '@/dashboard/patients/GlobalPatientSearchModal';
import {
  followUpTargetDate,
  proposedFollowUpDate,
  scheduleRowBookable,
  scheduleRowBookingIdentity,
  scheduleRowIdLabel,
} from '@/walkin/schedule-book-view';
import {
  dueDateLabel,
  dueDateTitle,
  dueStateForDate,
  dueUrgencyStep,
  type RowDueInfo,
} from '@/dashboard/coc/patient-followups-view';
import {
  workboardGuidance,
  workboardLeadHref,
} from '@/dashboard/workboard/workboard-view';

/**
 * AdoraCare - Receptionist "Today" home Open Follow-ups panel
 * (S1-P5-WORK-QUEUE-FIRST-SLICE-BUILD-01; evolved from the not-yet-configured
 * shell in S1-P2-RECEPTIONIST-HOME-TODAY-COMMAND).
 *
 * The TERTIARY (smallest) item on the home. It is now LIVE: it reads the
 * caller's Open Follow-ups from GET /api/me/work-actions (OPEN lead_actions
 * assigned to the caller's role or self, clinic-fenced server-side) and renders
 * each with Done / Dismiss controls that POST /api/work-actions/:id/transition.
 *
 * DATA HONESTY: no sample/dummy rows, no PHI. The response carries only the
 * carved name display string + ids / reason label / status / timestamps - never
 * a phone or clinical field. States are truthful: a loading note while the read
 * is in flight, an honest "No open follow-ups." empty state (a confirmed zero
 * from the live read), and an "Open follow-ups unavailable." note if the read
 * fails - never a fabricated row or count.
 *
 * Rendered via react-dom/server in tests (no jsdom): the default export's
 * fetch fires in an effect (which SSR does not run), so the static render is
 * the loading state. The presentational OpenFollowupsView + the pure
 * removeFollowupRow helper are exported so each state and the post-transition
 * row removal are unit-tested without a browser.
 *
 * "TO SCHEDULE" (DOCTOR-FLOW-AFTER-INTAKE-V1 D1): the same read also carries
 * DERIVED next-visit rows (finalized case sheets with a follow-up intent and
 * no active next booking). Rendered as a subsection UNDER the open rows, only
 * when non-empty. A derived row has NO Done/Dismiss: it clears itself once the
 * next appointment is booked, so its ONE door is "Book" (the /walkin
 * exact-appointment landing). PHI-free display fields only.
 */

/** Display contract for ONE Open Follow-ups row (mirrors the backend FollowupRow). */
export interface FollowupRow {
  /** lead_actions.id - the transition target. */
  id: number;
  /** The owning lead id. */
  leadId: number;
  /** Receptionist-facing patient name (carved lead name; '' when unknown). */
  patientName: string;
  /** Human label for why the follow-up is open (the workflow template). */
  reason: string;
  /** Human due context (first slice: "Assigned" - lead_actions has no due_at). */
  dueContext: string;
  /** The channel the follow-up goes out on (first slice: "Call"). */
  channel: string;
  /** lead_actions.status (always 'open' in this read). */
  status: string;
  /** Creation timestamp (ISO) or null. */
  createdAt: string | null;
  /** The lead's current canon lifecycle stage, or null (STITCH-2 SCOPE 5). */
  leadStage?: string | null;
}

/**
 * Display contract for ONE derived "To schedule" row (mirrors the backend
 * NextVisitScheduleRow, api/routes/work-actions.ts -- DOCTOR-FLOW-AFTER-
 * INTAKE-V1 D1). Derived, never stored: the row exists only while no active
 * next booking does, so booking is the only "done" act.
 */
export interface NextVisitScheduleRowView {
  readonly returnRequest?: ReturnSchedulingRequestView;
  /** The FINALIZED visit's appointment id (the /walkin exact-row landing). */
  appointmentId: number;
  caseSheetId: number;
  clinicId: number;
  /** Desk-facing display name ('' when unknown). */
  patientLabel: string;
  /** The doctor's follow-up intent token. */
  optionKey: string;
  /** Configured label for the token; null renders the raw token. */
  optionLabel: string | null;
  /** The doctor's booking instruction, when present. */
  note: string | null;
  /** The visit's treatment doctor roster name, or null (pre-apply / unset). */
  treatmentDoctorName: string | null;
  /** The sheet's finalize-time evidence (ISO) or null. */
  closedAt: string | null;
  /** LAKSHMI-5-BOOK-DOOR-CORRECTIVE-V1: the booking identity keys (already
   *  read by the derivation; now on the wire so Book can PREFILL the
   *  existing desk booking flow). Ids are admin keys, never PHI -- the
   *  sibling FollowupRow is lead-id-keyed on this same wire. Optional in
   *  the mirror so an older server degrades to a keyless (door-less) row. */
  patientId?: string | null;
  leadId?: number | null;
}

/** The two allowed transitions (claim-to-self is deferred). */
export type FollowupTransition = 'done' | 'dismissed';

/**
 * LAKSHMI-5 CORRECTIVE: Book -> the EXISTING desk booking flow
 * (RegisterBookingOffer -- the FD-20/D4 offer, REUSED never cloned) inside
 * the ONE work-modal shell, prefilled from the row's intent: identity key
 * (patient-first), the interval-proposed date opening the picker, the
 * doctor + note restated. Propose-then-confirm: the operator picks the
 * slot; booking lands -> the board opens on the LANDED day with the new
 * row highlighted (walkinBoardHref -- the T3 param's lawful use: a REAL
 * row on its landed day), and the queue row ceases to derive on the next
 * read. Esc/scrim-safe close books nothing. Own component so useNavigate
 * mounts ONLY here -- the panel's router-free SSR contract stands.
 */
function ScheduleBookModal({
  row,
  patientUmr,
  onClose,
  onBookedReload,
}: {
  row: NextVisitScheduleRowView;
  patientUmr?: string | null;
  onClose: () => void;
  onBookedReload: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [bookingBusy, setBookingBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  return (
    <ModalShell workspace trapFocus closeDisabled={bookingBusy}
      title={saved ? "Appointment booked" : "Book appointment"}
      onClose={onClose}
      testId="schedule-book-modal"
    >
      <RegisterBookingOffer
        bookingPresentation
        bookingIntent="appointment"
        patientName={row.patientLabel || 'Unknown patient'}
        headline={row.patientLabel || 'Unknown patient'}
        patientUmr={patientUmr}
        onBusyChange={setBookingBusy}
        onBookingSaved={() => setSaved(true)}
        {...scheduleRowBookingIdentity(row)}
        requestedReturn={row.returnRequest ? {doctorId: row.returnRequest.requestedDoctorId, durationMinutes: row.returnRequest.durationMinutes} : undefined}
        clinicId={row.clinicId}
        initialPickerDate={
          proposedFollowUpDate(row.optionKey, row.closedAt, Date.now(), row.returnRequest) ?? ''
        }
        onBooked={(appointmentId, bookedIso) => {
          onClose();
          onBookedReload();
          navigate(walkinBoardHref(bookedIso, appointmentId));
        }}
        onSkip={onClose}
      />
      {row.note || row.returnRequest ? <details data-testid="schedule-doctor-request">
        <summary>Doctor’s request</summary>
      {row.note ? (
        <p className="clinic-workbench__hint" data-testid="schedule-book-note">
          {"Doctor's note: " + row.note}
        </p>
      ) : null}
      {row.returnRequest ? <p>Requested doctor: {row.returnRequest.requestedDoctorName ?? (row.returnRequest.requestedDoctorId === null ? 'Not set' : 'Name unavailable')} · Time needed: {row.returnRequest.durationMinutes === null ? 'Not set' : `${row.returnRequest.durationMinutes} min`}</p> : null}
      </details> : null}
    </ModalShell>
  );
}

/**
 * Pure: drop the row with this id (used after a successful Done/Dismiss so the
 * resolved row leaves the list without a refetch). Returns a new array.
 */
export function removeFollowupRow(
  rows: readonly FollowupRow[],
  id: number,
): FollowupRow[] {
  return rows.filter((r) => r.id !== id);
}

type PanelState =
  | { kind: 'loading' }
  // scheduleRows optional so pre-D1 callers/fixtures stay valid; absent
  // renders exactly like [] (structural absence of the subsection).
  | { kind: 'ready'; rows: FollowupRow[]; scheduleRows?: NextVisitScheduleRowView[] }
  | { kind: 'error' };

/**
 * Existing row actions hosted by the shared task table. The default export
 * wires `onAction` to the same transition call.
 */
interface ContactPanelHost {
  followupNotice?: string | null;
  panelTarget?: HTMLElement | null;
  onPanelChange?: (open: boolean) => void;
  taskDisabled?: boolean;
}

function FollowupActions({
  row,
  busy,
  onAction,
  contactPanel,
  compact = false,
}: {
  contactPanel?: ContactPanelHost;
  compact?: boolean;
  row: FollowupRow;
  busy: boolean;
  onAction: (id: number, status: FollowupTransition) => void;
}): JSX.Element {
  const completionActions = <>
        <AcButton
          type="button"
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={() => onAction(row.id, 'done')}
        >
          Done
        </AcButton>
        <AcButton
          type="button"
          variant="ghost"
          size="sm"
          disabled={busy}
          onClick={() => onAction(row.id, 'dismissed')}
        >
          Dismiss
        </AcButton>
  </>;
  return (
      <div className="followups-row__actions followups-task__actions">
        {row.channel.toLowerCase() === 'call' && Number.isSafeInteger(row.leadId) && row.leadId > 0 ? (
          <LeadContactRevealControl {...contactPanel} key={`${row.id}:${row.leadId}`}
            availability="unknown" allowAttemptWhenUnknown polished
            followupTriggerLabel={compact ? 'Call' : undefined}
            initialPurpose="follow_up_call"
            followupPatientName={row.patientName || 'Unknown patient'}
            followupContext={row.reason}
            followupBusy={busy}
            followupFooter={<div className="followups-task__actions">
              <AcButton size="sm" disabled={busy} onClick={() => onAction(row.id, 'done')}>{busy ? 'Saving…' : 'Done'}</AcButton>
              <AcButton variant="ghost" size="sm" disabled={busy} onClick={() => onAction(row.id, 'dismissed')}>Dismiss</AcButton>
            </div>}
            reveal={(purpose) => revealLeadContact(String(row.leadId), purpose)} />
        ) : null}
        {compact ? <details className="followups-task__more">
          <summary aria-label={`More actions for ${row.patientName || 'Unknown patient'}`}>More</summary>
          <div className="followups-task__more-content">
            <span className="followups-row__next">{row.dueContext} · {row.channel}</span>
            <div className="followups-task__actions">{completionActions}</div>
          </div>
        </details> : completionActions}
      </div>
  );
}

/**
 * FRONT-DESK-DAY-BOARD-V1 (Partha TAB row "show recall due"): one
 * To-schedule row's recall-due fact, or null. DISPLAY of the recorded
 * intent only: the target day is THE ONE unclamped derivation
 * (followUpTargetDate -- the doctor's own recorded option applied to the
 * recorded close moment), and the standing + words + urgency step are the
 * COC register's OWN ratified due vocabulary (dueStateForDate /
 * dueDateLabel / dueUrgencyStep, patient-followups-view.ts) -- one due
 * grammar across the desks, never a second one. A row whose option
 * carries no structured interval renders NOTHING (honest absence; no
 * cadence is ever invented -- founder ruling 2026-08-21).
 */
export function scheduleRowRecallDue(
  row: Pick<NextVisitScheduleRowView, 'optionKey' | 'closedAt' | 'returnRequest'>,
  nowMs: number,
): RowDueInfo | null {
  const target = followUpTargetDate(row.optionKey, row.closedAt, nowMs, row.returnRequest);
  if (target === null) return null;
  return { dueDate: target, state: dueStateForDate(target, nowMs) };
}

/** Pure presentational view of the panel for a given state (testable via SSR). */
export function OpenFollowupsView({
  state,
  busyId,
  onAction,
  onQuickView,
  onBook,
  nowMs,
  contactPanel,
  compact = false,
  scheduleOnly = false,
  onRetry,
}: {
  onRetry?: () => void;
  scheduleOnly?: boolean;
  compact?: boolean;
  contactPanel?: ContactPanelHost;
  state: PanelState;
  busyId: number | null;
  onAction: (id: number, status: FollowupTransition) => void;
  onQuickView?: (row: FollowupRow) => void;
  /** LAKSHMI-5 CORRECTIVE: opens the prefilled booking flow for a
   *  To-schedule row. Absent (older tests) renders the button inert. */
  onBook?: (row: NextVisitScheduleRowView) => void;
  /** FRONT-DESK-DAY-BOARD-V1: the injected clock the recall-due standing is
   *  measured against (the D3 discipline -- nothing reads a clock during
   *  render). ABSENT = no due line (older callers byte-identical). */
  nowMs?: number;
}): JSX.Element {
  // D1: derived rows ride only the ready state; absent -> [] -> no subsection.
  const scheduleRows = state.kind === 'ready' ? state.scheduleRows ?? [] : [];
  return (
    <section className="rail-card followups-card" aria-label={scheduleOnly ? "To schedule" : "Open follow-ups"}>
      {!compact ? <h3 className="rail-card__title">{scheduleOnly ? "To schedule" : "Open Follow-ups"}</h3> : null}
      {state.kind === 'loading' ? (
        // V7-P4 adoption: the shared LoadingState (03 P11), copy unchanged.
        <LoadingState label={scheduleOnly ? "Loading next visits…" : "Loading open follow-ups..."} />
      ) : state.kind === 'error' ? (
        // V7-P4 adoption: the shared ErrorState (03 P11 S4), copy unchanged.
        <ErrorState title={scheduleOnly ? "Next visits unavailable." : "Open follow-ups unavailable."} action={scheduleOnly && onRetry ? <AcButton onClick={onRetry}>Retry</AcButton> : undefined} />
      ) : state.rows.length === 0 ? (
        // The panel count includes pending bookings as well as call tasks.
        // Only declare it empty when both sources have returned zero rows.
        scheduleRows.length === 0 ? <EmptyState
          icon={<BellRing size={20} aria-hidden="true" />}
          title={scheduleOnly ? "No visits to schedule." : "No open follow-ups."}
        /> : null
      ) : compact ? (
        <ul className="followups-list followups-list--docked" data-testid="followups-task-table">
          {state.rows.map((row) => (
            <li key={row.id} className="followups-task" data-testid={`followup-task-${row.id}`}>
              <Link className="followups-row__name" to={workboardLeadHref(row.leadId)}
                onClick={onQuickView === undefined ? undefined : (event) => {
                  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
                  event.preventDefault(); onQuickView(row);
                }}>{row.patientName || 'Unknown patient'}</Link>
              <span className="followups-task__reason">{row.reason}</span>
              <span className="followups-row__next followups-task__due">No due date</span>
              <FollowupActions compact row={row} busy={busyId !== null} onAction={onAction} contactPanel={contactPanel} />
            </li>
          ))}
        </ul>
      ) : (
        <DataTable
          label="Open follow-up tasks"
          testId="followups-task-table"
          rows={state.rows}
          rowKey={(row) => row.id}
          rowTestId={(row) => `followup-task-${row.id}`}
          rowClassName={() => 'followups-task'}
          columns={[
            {
              key: 'patient', header: 'Patient', width: '20%',
              render: (row) => (
                <Link className="followups-row__name" to={workboardLeadHref(row.leadId)}
                  onClick={onQuickView === undefined ? undefined : (e) => {
                    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
                    e.preventDefault();
                    onQuickView(row);
                  }}>
                  {row.patientName || 'Unknown patient'}
                </Link>
              ),
            },
            {
              key: 'followup', header: 'Follow-up', width: '24%',
              render: (row) => (
                <span className="followups-row__body">
                  <span className="followups-task__reason">{row.reason}</span>
                  <span className="followups-row__next">{workboardGuidance(row.reason).nextAction}</span>
                </span>
              ),
            },
            {
              key: 'contact', header: 'Contact', width: '12%',
              render: (row) => (
                <span className="followups-row__body">
                  <span>{row.channel}</span>
                  <span className="followups-row__next">{row.dueContext}</span>
                </span>
              ),
            },
            {
              key: 'actions', header: 'Actions', width: '44%',
              render: (row) => (
                <FollowupActions row={row} busy={busyId === row.id} onAction={onAction} contactPanel={contactPanel} />
              ),
            },
          ]}
        />
      )}
      {scheduleRows.length > 0 ? (
        // D1 subsection: NO Done/Dismiss on a derived row -- it ceases to
        // derive once the next appointment exists, so "Book" is the one door
        // (the T3 exact-appointment landing param; presentation-only).
        <section
          aria-label="To schedule"
          data-testid="followups-to-schedule"
          title="A row clears itself once the next appointment is booked."
        >
          {!scheduleOnly ? <h4 className="rail-card__title">To schedule</h4> : null}
          <ul className="followups-list">
            {scheduleRows.map((row) => (
              <li
                key={String(row.appointmentId)}
                className="followups-row"
                data-testid={`schedule-row-${row.appointmentId}`}
              >
                <span className="followups-row__body">
                  <span className="followups-row__name">
                    {row.patientLabel || 'Unknown patient'}
                  </span>
                  {/* TO-SCHEDULE-PATIENT-DISAMBIGUATION-V1: the SECOND
                      IDENTIFIER, directly under the name (the day board's
                      identity-caption position, dayBoardUmr). The finalized
                      visit's appointment number -- ALREADY on this wire and
                      UNIQUE per row -- so two same-name patients are told
                      apart BEFORE the operator clicks Book (the named live
                      defect, visual contract s.13). It is the same token the
                      Home search accepts ("appointment no."), so the operator
                      can cross-check it. */}
                  <span
                    className="followups-row__id"
                    data-testid={`schedule-id-${row.appointmentId}`}
                  >
                    {scheduleRowIdLabel(row.appointmentId)}
                  </span>
                  {/* FRONT-DESK-DAY-BOARD-V1 ("show recall due"): WHEN the
                      doctor's recorded intent falls due, in the COC
                      register's own ratified due grammar (value text always
                      prints; the urgency step is never the only carrier).
                      Absent when no clock is injected or the option carries
                      no structured interval -- honest absence. */}
                  {(() => {
                    if (typeof nowMs !== 'number') return null;
                    const due = scheduleRowRecallDue(row, nowMs);
                    if (due === null) return null;
                    const step = dueUrgencyStep(due.state);
                    return (
                      <span
                        className={`followups-row__recall${
                          step !== 'none' ? ` followups-row__recall--${step}` : ''
                        }`}
                        title={dueDateTitle(due)}
                        data-testid={`schedule-due-${row.appointmentId}`}
                      >
                        {dueDateLabel(due)}
                      </span>
                    );
                  })()}
                  <span className="followups-row__reason">
                    {'Next visit: ' +
                      (row.optionLabel ?? (scheduleOnly ? 'Follow-up details unavailable' : row.optionKey)) +
                      (row.returnRequest
                        ? (row.returnRequest.requestedDoctorName ? ' - requested with ' + row.returnRequest.requestedDoctorName : row.returnRequest.requestedDoctorId === null ? ' - requested doctor not set' : ' - requested doctor name unavailable') + (row.returnRequest.durationMinutes !== null ? ' - ' + row.returnRequest.durationMinutes + ' min requested' : '')
                        : row.treatmentDoctorName ? ' - with ' + row.treatmentDoctorName : '')}
                  </span>
                  {row.note ? (
                    <span className="followups-row__next">{row.note}</span>
                  ) : null}
                </span>
                <span className="followups-row__actions">
                  {/* LAKSHMI-5 CORRECTIVE: Book OPENS THE BOOKING FLOW
                      (RegisterBookingOffer, prefilled from this row's
                      intent). The old door pointed at the T3
                      exact-appointment landing with the FINISHED visit's
                      id -- a highlight-only param on today's board, so for
                      any past-day visit the click did NOTHING (India-lane
                      defect 2026-08-16). T3 itself stays untouched for
                      id-search. A keyless row renders NO door (LES-016) --
                      the operator books it through search. */}
                  {scheduleRowBookable(row) ? (
                    <button
                      type="button"
                      className="rail-card__link followups-row__btn"
                      data-testid={`schedule-book-${row.appointmentId}`}
                      onClick={() => onBook?.(row)}
                    >
                      Book
                    </button>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}

export default function WalkinFollowupsPanel({ contactPanel, compact = false, onSummary, schedulePatientId, schedulePatientUmr }: {
  contactPanel?: ContactPanelHost;
  compact?: boolean;
  schedulePatientId?: string;
  schedulePatientUmr?: string | null;
  onSummary?: (count: number | null, busy: boolean) => void;
} = {}): JSX.Element {
  const [state, setState] = useState<PanelState>({ kind: 'loading' });
  const [busyId, setBusyId] = useState<number | null>(null);
  const writeInFlight = useRef(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  // FRONT-DESK-DAY-BOARD-V1: the ONE injected clock for the recall-due
  // standing -- stamped at mount, never read during render (the D3
  // discipline). Minute drift on a mounted panel is immaterial for a
  // calendar-day standing.
  const [nowMs] = useState<number>(() => Date.now());
  // STITCH-2: the row-click quick view (the ONE drawer).
  const [quickViewRow, setQuickViewRow] = useState<FollowupRow | null>(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const quickViewTrigger = useRef<HTMLElement | null>(null);
  const closeQuickView = (): void => {
    if (detailBusy) return;
    setQuickViewRow(null);
    if (compact) contactPanel?.onPanelChange?.(false);
    requestAnimationFrame(() => quickViewTrigger.current?.focus());
  };
  // LAKSHMI-5 CORRECTIVE: the To-schedule row whose Book door is open (the
  // EXISTING RegisterBookingOffer inside the ONE work-modal shell).
  const [bookRow, setBookRow] = useState<NextVisitScheduleRowView | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchMyWorkActionsResult(schedulePatientId)
      .then((result) => {
        if (!cancelled) {
          setState({
            kind: 'ready',
            rows: schedulePatientId === undefined ? result.rows : [],
            scheduleRows: (result.scheduleRows ?? []).filter(row =>
              (schedulePatientId === undefined || row.patientId === schedulePatientId)),
          });
        }
      })
      .catch(() => {
        // Honest "unavailable" state rather than a fabricated empty/zero.
        if (!cancelled) setState({ kind: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [schedulePatientId]);

  useEffect(() => load(), [load]);

  useEffect(() => {
    onSummary?.(state.kind === 'ready' ? state.rows.length + (state.scheduleRows?.length ?? 0) : null, busyId !== null || detailBusy);
  }, [state, busyId, detailBusy, onSummary]);

  const onAction = useCallback(
    (id: number, status: FollowupTransition) => {
      if (writeInFlight.current) return;
      writeInFlight.current = true;
      setSaveError(null);
      setBusyId(id);
      transitionWorkAction(id, status)
        .then(() => {
          // The action left the open queue - drop it from the list (the
          // derived scheduleRows are untouched: booking, not Done, clears them).
          setState((prev) =>
            prev.kind === 'ready'
              ? { ...prev, rows: removeFollowupRow(prev.rows, id) }
              : prev,
          );
        })
        .catch(() => {
          setSaveError('Could not update the task. It is still open. Try again.');
        })
        .finally(() => {
          writeInFlight.current = false;
          setBusyId((b) => (b === id ? null : b));
        });
    },
    [],
  );

  const quickView = quickViewRow === null ? null : (
        <LeadQuickViewHost
          frame={compact ? 'panel' : 'drawer'}
          onBusyChange={setDetailBusy}
          leadId={quickViewRow.leadId}
          seedTitle={quickViewRow.patientName || 'Unknown patient'}
          onClose={closeQuickView}
          onChanged={() => load()}
          stageStrip={(model, reload) =>
            model.leadId !== null ? (
              <QuickViewStageStrip
                stage={model.stage}
                onAdvance={async (to) => {
                  setDetailBusy(true);
                  try {
                  await advanceLeadStage(model.leadId as number, to);
                  // The stage advance completed this lead's open tasks
                  // server-side; drop them from the open-only panel (derived
                  // scheduleRows untouched -- only booking clears those).
                  setState((prev) =>
                    prev.kind === 'ready'
                      ? {
                          ...prev,
                          rows: prev.rows.filter(
                            (r) => r.leadId !== (model.leadId as number),
                          ),
                        }
                      : prev,
                  );
                  reload();
                  } finally { setDetailBusy(false); }
                }}
              />
            ) : null
          }
        />
  );
  const dockedQuickView = quickView === null ? null : <WorkPanel title="Patient details"
    onClose={closeQuickView} closeDisabled={detailBusy} testId="task-patient-panel">{quickView}</WorkPanel>;
  return (
    <>
      {compact && contactPanel?.panelTarget ? createPortal(dockedQuickView, contactPanel.panelTarget) : quickView}
      {saveError ? <p role="alert">{saveError}</p> : null}
      <OpenFollowupsView
        scheduleOnly={schedulePatientId !== undefined}
        onRetry={load}
        state={state}
        compact={compact}
        contactPanel={{ ...contactPanel, followupNotice: saveError }}
        busyId={busyId}
        onAction={onAction}
        onQuickView={(row) => {
          quickViewTrigger.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          setQuickViewRow(row);
          if (compact) contactPanel?.onPanelChange?.(true);
        }}
        onBook={setBookRow}
        nowMs={nowMs}
      />
      {bookRow !== null ? (
        <ScheduleBookModal
          row={bookRow}
          patientUmr={schedulePatientUmr}
          onClose={() => setBookRow(null)}
          onBookedReload={load}
        />
      ) : null}

    </>
  );
}
